import React, { useState, useEffect, useCallback } from "react";
import PrintEstimator from "./PrintEstimator";
import AI3DModeling from "./AI3DModeling";
import CartItem from "./CartItem";
import PhoneAuthModal from "./PhoneAuthModal";
import CheckoutForm from "./CheckoutForm";
import { LogOut, User, LogIn, X, Package, Clock, CheckCircle, AlertCircle, CreditCard, Receipt, AlertTriangle, Upload, Bot } from "lucide-react";
import layableLogo from "./logo.png";
import * as ChannelService from "@channel.io/channel-web-sdk-loader";
const initialCart = [];

const PrintingPlatform = () => {
  const [cart, setCart] = useState(initialCart);
  const [totalPrice, setTotalPrice] = useState(0);
  const [shippingFee, setShippingFee] = useState(0);
  const [additionalServiceFee, setAdditionalServiceFee] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cartUuid, setCartUuid] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [orders, setOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [orderError, setOrderError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(true);
  const [showAuthErrorModal, setShowAuthErrorModal] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [showAddToCartModal, setShowAddToCartModal] = useState(false);
  const [estimateUuid, setEstimateUuid] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [activeTab, setActiveTab] = useState("stl"); // "stl" 또는 "ai"
  const [savedEstimatesContainer, setSavedEstimatesContainer] = useState(null);

  // Check for authentication on component mount and when token changes
  useEffect(() => {
    checkAuth();
  }, []);

  // Check for order parameter and handle order status
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get("mode");
    if (mode === "ai" || mode === "stl") {
      setActiveTab(mode);
    }
    const orderUuid = urlParams.get("order");
    if (orderUuid) {
      const checkOrderStatus = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/order/${orderUuid}`, {
            headers: {
              Authorization: localStorage.getItem("auth_token"),
            },
          });
          if (response.ok) {
            const orderData = await response.json();
            if (orderData.status === "처리중") {
              setOrderSuccess(true);
              setShowCheckoutForm(true);
              // Remove order parameter from URL
              const newUrl = window.location.pathname;
              window.history.replaceState({}, "", newUrl);
            } else if (response.status === 403) {
              setShowAuthErrorModal(true);
              // Remove order parameter from URL
              const newUrl = window.location.pathname;
              window.history.replaceState({}, "", newUrl);
            }
          }
        } catch (err) {
          console.error("주문 상태 확인 오류:", err);
        }
      };

      checkOrderStatus();
    }
  }, []);

  // 총 가격 계산 및 부가 비용 계산
  const updateTotalPrice = useCallback((cartItems) => {
    // 상품 기본 가격 계산
    const subtotal = cartItems.reduce((sum, item) => sum + item.estimated_cost * item.quantity, 0);

    // 배송비 계산
    const shipping = subtotal >= 30000 ? 0 : 3000;
    setShippingFee(shipping);

    // 추가 서비스 비용 계산 (색상 추가 비용)
    // 1. 장바구니의 모든 고유 색상 수집
    const uniqueColors = new Set();
    cartItems.forEach((item) => {
      if (item.color) {
        uniqueColors.add(item.color);
      }
    });

    // 2. 무료 색상 개수 계산 (기본 1개 + 3만원마다 1개 추가)
    const freeColorCount = 1 + Math.floor(subtotal / 30000);

    // 3. 추가 비용이 있는 색상 개수 계산
    const paidColorCount = Math.max(0, uniqueColors.size - freeColorCount);

    // 4. 추가 서비스 비용 계산 (색상당 3,000원)
    const additionalFee = paidColorCount * 3000;
    setAdditionalServiceFee(additionalFee);

    // 상품 기본 가격 업데이트
    setTotalPrice(subtotal);
    setFinalPrice(subtotal + shipping + additionalFee);
  }, []);

  // 장바구니에 아이템 추가
  const addToCart = useCallback(
    (estimate) => {
      setCart((prevCart) => {
        const existingItemIndex = prevCart.findIndex((item) => item.uuid === estimate.uuid);

        let newCart;
        if (existingItemIndex >= 0) {
          // 이미 장바구니에 있는 경우 수량 증가
          newCart = [...prevCart];
          newCart[existingItemIndex].quantity += 1;
        } else {
          // 새 아이템 추가
          newCart = [...prevCart, { ...estimate, quantity: 1 }];
        }

        // 총 가격 업데이트
        updateTotalPrice(newCart);
        return newCart;
      });
    },
    [updateTotalPrice]
  );

  // Check for uuid parameter and add to cart
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const estimateUuid = urlParams.get("uuid");
    if (estimateUuid) {
      const addEstimateToCart = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/estimate/${estimateUuid}`);
          if (response.ok) {
            const estimate = await response.json();
            if (estimate.success && estimate.data) {
              addToCart(estimate.data);
              // Remove uuid parameter from URL
              const newUrl = window.location.pathname;
              window.history.replaceState({}, "", newUrl);
            }
          }
        } catch (error) {
          console.error("Error adding estimate to cart:", error);
        }
      };

      addEstimateToCart();
    }
  }, [addToCart]);

  // Check authentication status
  const checkAuth = async () => {
    const token = localStorage.getItem("auth_token");

    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(process.env.REACT_APP_API_BASE + "/api/me", {
        method: "GET",
        headers: {
          Authorization: token,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // If token is invalid, remove it
        localStorage.removeItem("auth_token");
      }
    } catch (err) {
      console.error("Authentication check failed:", err);
      // If there's an error, remove the token
      localStorage.removeItem("auth_token");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    setUser(null);
    setShowCheckoutForm(false);
    setCartUuid(null);
  };

  // 장바구니 아이템 수량 변경
  const updateQuantity = (uuid, newQuantity) => {
    if (newQuantity < 1) return;

    const updatedCart = cart.map((item) => (item.uuid === uuid ? { ...item, quantity: newQuantity } : item));
    setCart(updatedCart);

    // 총 가격 업데이트
    updateTotalPrice(updatedCart);
  };

  // 장바구니에서 아이템 제거
  const removeFromCart = (uuid) => {
    const updatedCart = cart.filter((item) => item.uuid !== uuid);
    setCart(updatedCart);

    // 총 가격 업데이트
    updateTotalPrice(updatedCart);
  };

  // 인증 성공 처리
  const handleAuthSuccess = (userData) => {
    setShowAuthModal(false);
    setUser(userData);
    console.log("인증 성공!", userData);
  };

  // 장바구니 제출 처리
  const handleSubmitCart = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // 장바구니 아이템 준비
      const cartItems = cart.map((item) => ({
        uuid: item.uuid,
        quantity: item.quantity,
      }));

      // API 호출 URL 준비
      let apiUrl = process.env.REACT_APP_API_BASE + "/api/cart";
      if (appliedCoupon) {
        apiUrl += `?couponId=${appliedCoupon.couponId}`;
      }

      // API 호출
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("auth_token"),
        },
        body: JSON.stringify(cartItems),
      });

      const data = await response.json();

      if (response.ok) {
        setCartUuid(data.uuid);
        setShowCheckoutForm(true);
      } else {
        setError(data.detail || "장바구니 제출에 실패했습니다.");
      }
    } catch (err) {
      console.error("장바구니 제출 오류:", err);
      setError("서버 연결에 실패했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // 주문 내역 가져오기
  const fetchOrders = async () => {
    setIsLoadingOrders(true);
    setOrderError("");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/orders`, {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      } else {
        setOrderError("주문 내역을 불러오는데 실패했습니다.");
      }
    } catch (err) {
      console.error("주문 내역 조회 오류:", err);
      setOrderError("서버 연결에 실패했습니다.");
    } finally {
      setIsLoadingOrders(false);
    }
  };

  // 주문 상태에 따른 아이콘 및 색상 반환
  const getOrderStatusInfo = (status) => {
    switch (status) {
      case "결제대기":
        return {
          icon: <Clock className="w-5 h-5 text-yellow-500" />,
          text: "결제 대기 중",
          bgColor: "bg-yellow-50",
          textColor: "text-yellow-700",
          borderColor: "border-yellow-200",
        };
      case "처리중":
        return {
          icon: <Package className="w-5 h-5 text-blue-500" />,
          text: "처리중",
          bgColor: "bg-blue-50",
          textColor: "text-blue-700",
          borderColor: "border-blue-200",
        };
      case "완료":
        return {
          icon: <CheckCircle className="w-5 h-5 text-green-500" />,
          text: "배송됨",
          bgColor: "bg-green-50",
          textColor: "text-green-700",
          borderColor: "border-green-200",
        };
      case "취소":
        return {
          icon: <AlertCircle className="w-5 h-5 text-red-500" />,
          text: "취소됨",
          bgColor: "bg-red-50",
          textColor: "text-red-700",
          borderColor: "border-red-200",
        };
      default:
        return {
          icon: <AlertCircle className="w-5 h-5 text-gray-500" />,
          text: status,
          bgColor: "bg-gray-50",
          textColor: "text-gray-700",
          borderColor: "border-gray-200",
        };
    }
  };

  // 결제 정보 가져오기
  const fetchPaymentInfo = async (payCode) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_PAY_BASE}/api/payment/${payCode}/paid`);
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error("결제 정보 조회 오류:", error);
      return null;
    }
  };

  // 결제 수단 정보 반환
  const getPaymentMethodInfo = (paymentData) => {
    if (!paymentData) return { text: "결제 정보 없음", icon: <AlertCircle className="w-5 h-5 text-gray-500" /> };

    const { publisher, cardname, type, brand, owner_type, number } = paymentData;

    let icon = <CreditCard className="w-5 h-5 text-blue-500" />;
    let text = "";
    let details = "";

    if (type === "CREDIT") {
      const ownerTypeText = owner_type === "PERSONAL" ? "개인" : "법인";
      text = `${cardname || publisher || "카드"} 결제`;
      details = `${ownerTypeText}신용(${brand}) ${number}`;
    } else if (type === "DEBIT") {
      const ownerTypeText = owner_type === "PERSONAL" ? "개인" : "법인";
      text = `${cardname || publisher || "카드"} 결제`;
      details = `${ownerTypeText}체크(${brand}) ${number}`;
    }

    return { text, icon, details };
  };

  // 주문 내역 모달 열기
  const handleOpenOrderHistory = () => {
    setShowOrderHistory(true);
    setSelectedOrder(null);
    fetchOrders();
  };

  // 주문 상세 정보 보기
  const handleViewOrderDetail = async (order) => {
    setSelectedOrder(order);

    // 결제 정보 가져오기
    if (order.pay_code) {
      const paymentInfo = await fetchPaymentInfo(order.pay_code);
      setSelectedOrder((prev) => ({
        ...prev,
        paymentInfo,
      }));
    }
  };

  // 주문 상세 정보 닫기
  const handleCloseOrderDetail = () => {
    setSelectedOrder(null);
  };

  // 쿠폰 적용 함수
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("쿠폰 코드를 입력해주세요");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/coupon/${couponCode}`, {
        headers: {
          Authorization: localStorage.getItem("auth_token"),
        },
      });

      if (response.ok) {
        const couponData = await response.json();
        if (couponData.availableCount === 0) {
          setAppliedCoupon(null);
          setCouponError("만료된 쿠폰 코드입니다");
          return;
        }
        setAppliedCoupon(couponData);
        setCouponError("");
        updateFinalPrice(couponData);
      } else {
        setAppliedCoupon(null);
        setCouponError("유효하지 않은 쿠폰 코드입니다");
      }
    } catch (err) {
      console.error("쿠폰 적용 오류:", err);
      setAppliedCoupon(null);
      setCouponError("쿠폰 적용 중 오류가 발생했습니다");
    }
  };

  // 쿠폰 제거 함수
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
    updateFinalPrice(null);
  };

  // 최종 가격 업데이트 함수
  const updateFinalPrice = (coupon) => {
    let discount = 0;
    const totalAmount = totalPrice + shippingFee + additionalServiceFee;

    if (coupon) {
      if (coupon.type === "PERCENTAGE") {
        discount = (totalAmount * coupon.discountAmount) / 100;
      } else if (coupon.type === "FIXED_AMOUNT") {
        discount = coupon.discountAmount;
      }
    }

    const finalPrice = totalAmount - discount;
    setFinalPrice(finalPrice);
  };

  // 장바구니/배송/추가비용/쿠폰 변경 시 최종 금액을 자동으로 재계산하여 할인 적용 유지
  useEffect(() => {
    let discount = 0;
    const totalAmount = totalPrice + shippingFee + additionalServiceFee;
    if (appliedCoupon) {
      if (appliedCoupon.type === "PERCENTAGE") {
        discount = (totalAmount * appliedCoupon.discountAmount) / 100;
      } else if (appliedCoupon.type === "FIXED_AMOUNT") {
        discount = appliedCoupon.discountAmount;
      }
    }
    setFinalPrice(totalAmount - discount);
  }, [totalPrice, shippingFee, additionalServiceFee, appliedCoupon]);

  // 장바구니 제목 클릭 핸들러
  const handleTitleClick = () => {
    setClickCount((prev) => prev + 1);
    if (clickCount + 1 === 5) {
      setShowAddToCartModal(true);
      setClickCount(0);
    }
  };

  // UUID로 상품 추가 핸들러
  const handleAddToCart = async () => {
    if (!estimateUuid) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/estimate/${estimateUuid}`);
      if (response.ok) {
        const estimate = await response.json();
        if (estimate.success && estimate.data) {
          addToCart(estimate.data);
          setShowAddToCartModal(false);
          setEstimateUuid("");
          setModalMessage("상품이 성공적으로 추가되었습니다.");
          setShowSuccessModal(true);
        }
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      setModalMessage("상품 추가에 실패했습니다.");
      setShowErrorModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 섹션 */}
      {/* <header class="fixed z-50 w-full bg-white transition-shadow">
        <div class="container mx-auto px-6">
          <nav class="flex h-20 items-center justify-between">
            <div class="flex items-center gap-2">
              <a href="/">
                <img src={layableLogo} alt="logo" class="w-28 md:w-32" />
              </a>
            </div>

            <div class="hidden items-center gap-8 md:flex">
              <button class="cursor-pointer rounded-lg bg-gray-900 px-6 py-2 text-white transition-colors hover:bg-black">문의하기</button>
            </div>
          </nav>
        </div>
      </header> */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-5 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <a href="//layable.kr">
              <img src={layableLogo} alt="logo" className="w-28 md:w-32" />
            </a>
            <h1 className="text-xl font-bold text-gray-400">3D 제작대행</h1>
          </div>
          <div className="flex items-center space-x-4">
            <button className="cursor-pointer rounded-lg bg-gray-900 px-6 py-2 text-white transition-colors hover:bg-black" onClick={() => (window.location.href = "https://layable.kr")}>
              홈페이지
            </button>
          </div>
        </div>
      </header>

      {/* Hero 섹션 */}
      <section className="relative py-24 overflow-hidden">
        {/* 배경 패턴 */}
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 to-white"></div>

        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 mx-1">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-gray-900">
                당신의 디자인을
                <br />
                <span className="text-teal-500">실물로 만들어보세요</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-lg">
                전문적인 제작 서비스로
                <br />
                아이디어를 현실로 만드는 경험을 제공합니다
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button onClick={() => document.getElementById("estimate").scrollIntoView({ behavior: "smooth" })} className="bg-teal-500 text-white px-8 py-4 rounded-lg font-medium hover:bg-teal-600 transition-colors text-lg shadow-lg hover:shadow-xl">
                  견적 계산하기
                </button>
                <button
                  onClick={() => {
                    ChannelService.openChat();
                  }}
                  className="bg-white text-teal-500 px-8 py-4 rounded-lg font-medium hover:bg-teal-50 transition-colors text-lg border-2 border-teal-500"
                >
                  문의하기
                </button>
              </div>
            </div>
            <div class="relative hidden lg:block">
              <div class="relative z-10 overflow-hidden rounded-3xl shadow-2xl"></div>
              <div class="absolute -bottom-6 -left-6 h-24 w-24 rounded-full bg-teal-500 opacity-20"></div>
              <div class="absolute -top-6 -right-6 h-32 w-32 rounded-full bg-teal-500 opacity-10"></div>
            </div>

            <div className="hidden ">
              <div className="relative">
                <div className="absolute inset-0 bg-teal-100/30 rounded-2xl transform rotate-6"></div>
                <div className="relative bg-white rounded-2xl p-4 shadow-xl">
                  <div className="aspect-w-16 aspect-h-9">
                    <img src="https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1964&q=80" alt="제작 서비스" className="rounded-lg object-cover" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 견적 계산 섹션 */}
      <section id="estimate" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center mb-4">
            <h2 className="text-3xl font-bold text-gray-900">견적 계산</h2>
          </div>

          {/* 탭 네비게이션 */}
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 rounded-lg p-1 flex">
              <button onClick={() => setActiveTab("stl")} className={`px-6 py-3 rounded-md font-medium transition-all ${activeTab === "stl" ? "bg-white text-teal-600 shadow-sm" : "text-gray-600 hover:text-gray-800"}`}>
                <Upload className="w-5 h-5 inline mr-2" />
                파일 업로드
              </button>
              <button onClick={() => setActiveTab("ai")} className={`px-6 py-3 rounded-md font-medium transition-all ${activeTab === "ai" ? "bg-white text-teal-600 shadow-sm" : "text-gray-600 hover:text-gray-800"}`}>
                <Bot className="w-5 h-5 inline mr-2" />
                AI 3D 모델링
              </button>
            </div>
          </div>

          {/* 탭별 설명 */}
          <div className="text-center mb-12">{activeTab === "stl" ? <p className="text-gray-600">STL 또는 3MF 파일을 업로드하고 즉시 견적을 받아보세요</p> : <p className="text-gray-600">이미지를 업로드하여 AI로 3D 모델을 생성하고 견적을 받아보세요</p>}</div>

          <div className="relative">
            {/* Warning Modal */}
            {showWarningModal && (
              <div className="absolute backdrop-blur-sm inset-0 flex items-center justify-center z-10">
                <div className="bg-white rounded-xl shadow-2xl w-full p-6 mx-2 md:p-8 md:mx-8">
                  <div className="flex items-center mb-6">
                    <AlertTriangle className="w-6 h-6 text-teal-500 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">{activeTab === "stl" ? "파일 업로드 시스템 이용 전 꼭 확인해 주세요" : "AI 3D 모델링 이용 전 꼭 확인해 주세요"}</h2>
                  </div>

                  <div className="space-y-6 text-gray-700">
                    {activeTab === "stl" ? (
                      // STL 파일 업로드 안내사항
                      <>
                        <div className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                            <span className="text-teal-500 font-medium">1</span>
                          </div>
                          <div>
                            <p className="font-medium mb-1">지원 파일 형식</p>
                            <p className="text-sm text-gray-500">STL, 3MF 파일만 업로드 가능합니다.</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                            <span className="text-teal-500 font-medium">2</span>
                          </div>
                          <div>
                            <p className="font-medium mb-1">크기 제한</p>
                            <p className="text-sm text-gray-500">250mm × 250mm 이상의 출력물은 제작이 불가능합니다. 파일 업로드 전 모델 크기를 반드시 확인해 주세요.</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                            <span className="text-teal-500 font-medium">3</span>
                          </div>
                          <div>
                            <p className="font-medium mb-1">단위(스케일) 설정</p>
                            <p className="text-sm text-gray-500">STL 파일의 단위가 올바르게 설정되어 있지 않으면, 오류가 나거나 출력 크기가 달라질 수 있습니다. 모델링 소프트웨어에서 단위(mm 등)를 확인한 뒤 파일을 업로드해 주세요.</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                            <span className="text-teal-500 font-medium">4</span>
                          </div>
                          <div>
                            <p className="font-medium mb-1">구조적 문제</p>
                            <p className="text-sm text-gray-500">업로드된 모델에 구조적 문제가 있어 출력이 어렵다고 판단될 경우, 내부 검토 후 카카오톡, SMS, 이메일 등을 통해 개별 연락을 드릴 수 있습니다.</p>
                          </div>
                        </div>
                      </>
                    ) : (
                      // AI 3D 모델링 안내사항
                      <>
                        <div className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                            <span className="text-teal-500 font-medium">1</span>
                          </div>
                          <div>
                            <p className="font-medium mb-1">풀 컬러 제작 관련</p>
                            <p className="text-sm text-gray-500">AI 생성 모델의 경우 풀 컬러로 제작이 진행되며, 레진 소재를 사용하여 제작합니다. 미리보기에 나오는 색상과 다소 다를 수 있으나, 정교한 컬러 제작이 가능합니다.</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                            <span className="text-teal-500 font-medium">2</span>
                          </div>
                          <div>
                            <p className="font-medium mb-1">제작 크기 관련</p>
                            <p className="text-sm text-gray-500">AI가 생성한 모델 특성상, 크기가 정해지지 않습니다. 기본적으로 형상과 무관하게 가장 긴 선을 기준으로 5cm 사이즈로 제작합니다.</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                            <span className="text-teal-500 font-medium">3</span>
                          </div>
                          <div>
                            <p className="font-medium mb-1">지원 이미지</p>
                            <p className="text-sm text-gray-500">JPG, PNG 형식의 이미지만 업로드 가능하며, 1장에서 4장까지의 이미지를 업로드할 수 있습니다. 다양한 각도의 이미지를 제공하면 더 정확한 3D 모델을 생성할 수 있습니다.</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                            <span className="text-teal-500 font-medium">4</span>
                          </div>
                          <div>
                            <p className="font-medium mb-1">이미지 품질</p>
                            <p className="text-sm text-gray-500">선명하고 배경이 단순한 이미지일수록 더 좋은 결과를 얻을 수 있습니다. 물체가 화면에 크게 나오도록 촬영해 주세요.</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="w-6 h-6 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                            <span className="text-teal-500 font-medium">5</span>
                          </div>
                          <div>
                            <p className="font-medium mb-1">생성 시간</p>
                            <p className="text-sm text-gray-500">AI 3D 모델 생성에는 약 3-5분 정도 소요됩니다. 생성 완료 후 문자메세지로 안내드립니다.</p>
                          </div>
                        </div>
                      </>
                    )}

                    <div class="flex items-start">
                      <div class="w-6 h-6 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                        <span class="text-teal-500 font-medium">!</span>
                      </div>
                      <div>
                        <p class="text-sm text-gray-800 mb-2">결제 전 자동 생성된 미리보기를 꼭 확인해 주세요.</p>
                        <p class="text-sm text-gray-800 mb-2">주문제작 상품 특성상 배송까지 평균 3일, 최대 10일까지 소요될 수 있습니다. (풀컬러 제작: 최대 1개월)</p>
                        <p class="text-sm text-gray-800 ">업로드 하시는 모든 파일은 고객님께서 적법한 이용 권한을 가진 자료여야 하며, 당사는 권리관계에 관여하지 않습니다.</p>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-teal-50 rounded-lg">
                      <p className="text-sm text-gray-600">사전 확인이 필요한 사항이나 추가 문의사항은 우측 하단의 채널톡으로 연락주세요.</p>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end">
                    <button onClick={() => setShowWarningModal(false)} className="px-6 py-3 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors">
                      확인
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid gap-6 md:grid-cols-2">
              {/* 왼쪽: 탭별 컴포넌트 */}
              <div className="h-full">
                {activeTab === "stl" ? (
                  <PrintEstimator
                    onEstimateComplete={addToCart}
                    onShowInfo={() => setShowWarningModal(true)}
                    user={user}
                    savedEstimatesContainer={savedEstimatesContainer}
                    renderSavedEstimatesInline={false}
                  />
                ) : (
                  <AI3DModeling onAddToCart={addToCart} onRequestLogin={() => setShowAuthModal(true)} user={user} onShowInfo={() => setShowWarningModal(true)} />
                )}
              </div>

              <div className="h-full bg-white rounded-xl shadow-lg p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" onClick={handleTitleClick}>
                    장바구니
                  </h2>
                  {isLoading ? (
                    <div className="h-6 w-24 bg-gray-200 rounded animate-pulse"></div>
                  ) : user ? (
                    <div className="relative group">
                      <button className="flex items-center space-x-1 text-sm text-gray-600 hover:text-blue-600 transition-colors">
                        <User className="w-4 h-4" />
                        <span className="font-medium">{user.name || user.phone}</span>
                      </button>
                      <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg py-1 z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                        <button onClick={handleOpenOrderHistory} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                          <Package className="w-4 h-4 mr-2" />
                          주문 내역
                        </button>
                        <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                          <LogOut className="w-4 h-4 mr-2" />
                          로그아웃
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setShowAuthModal(true)} className="flex items-center space-x-1 text-sm text-teal-500 hover:text-teal-600 transition-colors">
                      <LogIn className="w-4 h-4" />
                      <span className="font-medium">로그인</span>
                    </button>
                  )}
                </div>

                {cart.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>장바구니가 비어있습니다</p>
                    <p className="text-sm mt-2">견적을 계산하여 장바구니에 추가해보세요</p>
                  </div>
                ) : (
                  <>
                    <div className="max-h-[595px] overflow-y-auto mb-4">
                      {cart.map((item) => (
                        <CartItem key={item.uuid} item={item} onRemove={removeFromCart} onUpdateQuantity={updateQuantity} />
                      ))}
                    </div>

                    <div className="border-t pt-4">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex justify-between items-center text-gray-500 text-sm">
                          <div className="flex items-center">
                            <span>견적 금액</span>
                          </div>
                          <span>{`${totalPrice.toLocaleString()}원`}</span>
                        </div>
                        <div className="flex justify-between items-center text-gray-500 text-sm">
                          <div className="flex items-center">
                            <span>배송비</span>
                            <div className="relative ml-1 group">
                              <span className="cursor-help">?</span>
                              <div className="absolute z-10 -left-10 mt-2 w-64 bg-white rounded-md shadow-lg p-3 text-sm text-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-gray-200">3만원 이상 주문 시 무료배송, 그 외 3,000원의 배송비가 부과됩니다.</div>
                            </div>
                          </div>
                          <span>{totalPrice <= 30000 ? `${shippingFee.toLocaleString()}원` : "무료"}</span>
                        </div>

                        <div className="flex justify-between items-center text-gray-500 text-sm">
                          <div className="flex items-center">
                            <span>추가 서비스 비용</span>
                            <div className="relative ml-1 group">
                              <span className="cursor-help">?</span>
                              <div className="absolute z-10 -left-24 mt-2 w-64 bg-white rounded-md shadow-lg p-3 text-sm text-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-gray-200">
                                기본 색상 1개는 무료이며, 색상 추가 시 개당 3,000원이 부과됩니다. <br></br> 견적 금액 3만원마다 색상 1개를 추가로 무료 선택하실 수 있습니다.
                              </div>
                            </div>
                          </div>
                          <span>{additionalServiceFee === 0 ? "무료" : `${additionalServiceFee.toLocaleString()}원`}</span>
                        </div>

                        {/* 쿠폰 섹션 수정 */}
                        {!appliedCoupon && (
                          <div className="mt-1">
                            <button onClick={() => setShowCouponInput(!showCouponInput)} className="w-full text-left text-sm text-teal-500 hover:text-teal-600 flex items-center justify-between">
                              <span>할인 코드 입력</span>
                              <svg className={`w-4 h-4 transform transition-transform ${showCouponInput ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {showCouponInput && (
                              <div className="mt-2">
                                <div className="flex items-center gap-2">
                                  <input type="text" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} placeholder="할인 코드 입력" className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
                                  <button onClick={applyCoupon} className="px-3 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 transition-colors">
                                    적용
                                  </button>
                                </div>
                                {couponError && <p className="mt-1 text-sm text-red-500">{couponError}</p>}
                              </div>
                            )}
                          </div>
                        )}

                        {appliedCoupon && (
                          <div className="mt-2">
                            <div className="flex justify-between items-center text-sm">
                              <div className="flex items-center">
                                <span className="text-teal-500">{appliedCoupon.couponName}</span>
                                <button onClick={removeCoupon} className="ml-2 text-gray-400 hover:text-gray-600">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <span className="text-teal-500">{appliedCoupon.type === "PERCENTAGE" ? `-${appliedCoupon.discountAmount}%` : `-${appliedCoupon.discountAmount.toLocaleString()}원`}</span>
                            </div>
                          </div>
                        )}

                        <div className="flex justify-between items-center mt-2 mb-4">
                          <span className="text-lg font-bold">총 금액</span>
                          <div className="flex flex-col items-end">
                            {appliedCoupon && <span className="text-lg text-gray-400 line-through">{(totalPrice + shippingFee + additionalServiceFee).toLocaleString()}원</span>}
                            <span className="text-lg font-bold text-teal-500">{finalPrice.toLocaleString()}원</span>
                          </div>
                        </div>
                      </div>

                      {error && <div className="mb-4 p-3 bg-red-50 rounded-lg text-red-700 border border-red-200 text-sm">{error}</div>}

                      <button onClick={handleSubmitCart} disabled={isSubmitting} className="w-full bg-teal-500 text-white py-4 text-lg rounded-lg font-medium hover:bg-teal-600 disabled:bg-gray-400 disabled:cursor-not-allowed inline-flex items-center justify-center">
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            처리 중...
                          </>
                        ) : (
                          "다음으로 →"
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
              {activeTab === "stl" && <div ref={setSavedEstimatesContainer} className="md:col-span-2" />}
            </div>
          </div>
        </div>
      </section>

      {/* 휴대폰 인증 모달 */}
      {showAuthModal && <PhoneAuthModal onClose={() => setShowAuthModal(false)} onSuccess={handleAuthSuccess} />}

      {/* 인증 에러 모달 */}
      {showAuthErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">로그인 정보 만료</h2>
              <button onClick={() => setShowAuthErrorModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-gray-600">로그인 정보가 만료되어 주문정보를 불러올 수 없습니다.</p>
              <p className="text-gray-600">문자메세지로 전송된 주문내역을 확인하거나, 로그인 후 장바구니→주문내역에서 정상 주문 여부를 확인해주세요.</p>
              <div className="flex justify-end space-x-3 mt-6">
                <button onClick={() => setShowAuthErrorModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800">
                  닫기
                </button>
                <button
                  onClick={() => {
                    setShowAuthErrorModal(false);
                    setShowAuthModal(true);
                  }}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
                >
                  로그인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 체크아웃 폼 */}
      {showCheckoutForm && (
        <CheckoutForm
          cartUuid={cartUuid}
          onClose={() => {
            setShowCheckoutForm(false);
            setOrderSuccess(false);
          }}
          isOrderSuccess={orderSuccess}
        />
      )}

      {/* 주문 내역 모달 */}
      {showOrderHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center mb-6 flex-shrink-0">
              <h2 className="text-2xl font-bold">주문 내역</h2>
              <button onClick={() => setShowOrderHistory(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {orderError && <div className="mb-4 p-3 bg-red-50 rounded-lg text-red-700 border border-red-200 text-sm flex-shrink-0">{orderError}</div>}

            <div className="overflow-y-auto flex-grow">
              {isLoadingOrders ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg">주문 내역이 없습니다</p>
                  <p className="text-sm mt-2">견적을 계산하여 주문해보세요</p>
                </div>
              ) : selectedOrder ? (
                <div>
                  <div className="flex items-center mb-4">
                    <button onClick={handleCloseOrderDetail} className="text-blue-600 hover:text-blue-800 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                      목록으로 돌아가기
                    </button>
                  </div>

                  <div className={`border rounded-lg overflow-hidden mb-3 mx-1 ${getOrderStatusInfo(selectedOrder.status).borderColor}`}>
                    <div className={`p-4 ${getOrderStatusInfo(selectedOrder.status).bgColor} flex justify-between items-center`}>
                      <div className="flex items-center">
                        {getOrderStatusInfo(selectedOrder.status).icon}
                        <span className={`ml-2 font-medium ${getOrderStatusInfo(selectedOrder.status).textColor}`}>{getOrderStatusInfo(selectedOrder.status).text}</span>
                      </div>
                      <div className="text-sm text-gray-500">{new Date(selectedOrder.created_at).toLocaleString()}</div>
                    </div>

                    <div className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <h4 className="font-medium mb-2">주문 정보</h4>
                          <div className="text-sm space-y-1">
                            <p>
                              <span className="text-gray-500">주문자:</span> {selectedOrder.user.name}
                            </p>
                            <p>
                              <span className="text-gray-500">주문명:</span> {selectedOrder.orderName || "주문"}
                            </p>
                            <p>
                              <span className="text-gray-500">주문번호:</span> {selectedOrder.uuid}
                            </p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">배송 정보</h4>
                          <div className="text-sm space-y-1">
                            <p>
                              <span className="text-gray-500">주소:</span> [{selectedOrder.address.zonecode}] {selectedOrder.address.address} {selectedOrder.address.address_detail}
                            </p>
                            <p>
                              <span className="text-gray-500">연락처:</span> {selectedOrder.user.phone.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3")}
                            </p>
                            <p>
                              <span className="text-gray-500">운송장번호:</span> {selectedOrder.tracking_no || "배송 준비중"}
                              {selectedOrder.tracking_no?.startsWith("로젠택배") && (
                                <>
                                  {""}
                                  <a href={`https://www.ilogen.com/web/personal/trace/${selectedOrder.tracking_no.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-600 hover:text-blue-800 text-sm inline-flex items-center">
                                    배송조회
                                  </a>
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* 결제 정보 섹션 추가 */}
                      {selectedOrder.paymentInfo && (
                        <div className="mb-4 border-t pt-4">
                          <h4 className="font-medium mb-2">결제 정보</h4>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center">
                              {getPaymentMethodInfo(selectedOrder.paymentInfo).icon}
                              <span className="ml-2 font-medium text-gray-800">{getPaymentMethodInfo(selectedOrder.paymentInfo).text}</span>
                              {selectedOrder.paymentInfo.receipt_url && (
                                <a href={selectedOrder.paymentInfo.receipt_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:text-blue-800 text-sm inline-flex items-center">
                                  <Receipt className="w-4 h-4 mr-1" />
                                  매출전표
                                </a>
                              )}
                            </div>
                            <div className="ml-7 mb-2 text-xs text-gray-500">{getPaymentMethodInfo(selectedOrder.paymentInfo).details}</div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm mt-4">
                              <div className="md:border-r md:pr-3 md:text-center">
                                <p className="mb-1">
                                  <span className="text-gray-500">결제일시:</span> {new Date(selectedOrder.paymentInfo.paid_at).toLocaleString()}
                                </p>
                              </div>
                              <div className="md:border-r md:pr-3 md:text-center">
                                <p className="mb-1">
                                  <span className="text-gray-500">승인번호:</span> {selectedOrder.paymentInfo.approval_no}
                                </p>
                              </div>
                              <div className="md:pr-3 md:text-center">
                                <p className="mb-1">
                                  <span className="text-gray-500">결제금액:</span> <span className="font-medium">{selectedOrder.paymentInfo.amount.toLocaleString()}원</span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <h4 className="font-medium mb-2">주문 상품</h4>
                      <div className="space-y-3">
                        {selectedOrder.cart.items.map((item, index) => (
                          <CartItem key={index} item={item.item} editable={false} onRemove={() => {}} onUpdateQuantity={() => {}} initialQuantity={item.quantity} />
                        ))}
                      </div>

                      <div className="mt-4 pt-4 border-t">
                        <div className="flex justify-between text-sm">
                          <span className="whitespace-nowrap">상품 금액</span>
                          <span className="whitespace-nowrap">{selectedOrder?.cart.subtotal.toLocaleString()}원</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="whitespace-nowrap">추가 서비스 비용</span>
                          <span className="whitespace-nowrap">{selectedOrder?.cart.additional_fee === 0 ? "무료" : `${selectedOrder?.cart.additional_fee.toLocaleString()}원`}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="whitespace-nowrap">배송비</span>
                          <span className="whitespace-nowrap">{selectedOrder?.cart.shipping_fee === 0 ? "무료" : `${selectedOrder?.cart.shipping_fee.toLocaleString()}원`}</span>
                        </div>
                        <div className="flex justify-between font-medium mt-2 pt-2 border-t">
                          <span className="whitespace-nowrap">총 결제 금액</span>
                          <span className="text-teal-500 whitespace-nowrap">{selectedOrder?.cart.total_cost.toLocaleString()}원</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 mx-1">
                  {orders.map((order) => {
                    const statusInfo = getOrderStatusInfo(order.status);

                    return (
                      <div key={order.uuid} className={`border rounded-lg overflow-hidden ${statusInfo.borderColor} cursor-pointer hover:shadow-md transition-shadow`} onClick={() => handleViewOrderDetail(order)}>
                        <div className={`p-3 ${statusInfo.bgColor} flex justify-between items-center`}>
                          <div className="flex items-center">
                            {statusInfo.icon}
                            <span className={`ml-2 font-medium ${statusInfo.textColor}`}>{statusInfo.text}</span>
                          </div>
                          <div className="text-sm text-gray-500">{new Date(order.created_at).toLocaleString()}</div>
                        </div>

                        <div className="p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <div className="w-full max-w-">
                                <h3 className="font-medium">{order.orderName || "주문"}</h3>
                                <p className="text-sm text-gray-500 mt-1">주문번호: {order.uuid}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-lg whitespace-nowrap">{order.cart.total_cost.toLocaleString()}원</p>
                              <p className="text-sm text-gray-500 mt-1">{order.cart.items.length}개 상품</p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between text-sm text-blue-600">
                            <span>상세 정보 보기</span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add to Cart Modal */}
      {showAddToCartModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">상품 추가</h2>
              <button onClick={() => setShowAddToCartModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">견적 UUID</label>
                <input type="text" value={estimateUuid} onChange={(e) => setEstimateUuid(e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500" placeholder="견적 UUID를 입력하세요" />
              </div>
              <div className="flex justify-end space-x-3">
                <button onClick={() => setShowAddToCartModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800">
                  취소
                </button>
                <button onClick={handleAddToCart} className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600">
                  추가
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-teal-600">성공</h2>
              <button onClick={() => setShowSuccessModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-700">{modalMessage}</p>
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowSuccessModal(false)} className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600">
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-red-600">오류</h2>
              <button onClick={() => setShowErrorModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-700">{modalMessage}</p>
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowErrorModal(false)} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintingPlatform;
